/**
 * Ticket rendering for the browser, ported from the Android app.
 *
 * The printers are fed a raster image, not text: TicketRenderer.java lays the ticket out on a
 * Bitmap and EscPosPrinter.java ships it as a GS v 0 raster. This file reproduces that layout on a
 * canvas so the PWA can build its own ticket bytes and hand them to the server, which prints them
 * over the tunnel. That removes the PWA's dependence on an Android device being on the shop LAN.
 *
 * Layout numbers, wrapping rules and setting names are deliberately copied from the Java rather
 * than tidied, so the two stay comparable when either side changes.
 */
import type {
  Category,
  OrderEvent,
  OrderLine,
  OrderRecord,
  Orders,
  PrinterConfig,
  Product,
  SalesPeriodReport,
  SettingRow,
  Setup
} from "./types";
import { optionLabels } from "./utils.ts";

const WIDTH_80MM = 576;
const PADDING_80MM = 24;
const PADDING_58MM = 18;

export type PrintProfile = "order" | "history";

export type TicketItem = {
  quantity: number;
  name: string;
  notes: string;
  unitPrice: number;
  productId: number;
  categoryName: string;
  categorySortOrder: number;
};

export type Ticket = {
  title: string;
  table: string;
  waiter: string;
  generalNotes: string;
  orderNumber: number;
  createdAt: number;
  items: TicketItem[];
};

export type RenderOptions = {
  paperWidthPx: number;
  textScale: number;
  detailTextScale: number;
  itemSpacingPx: number;
  eventTitleFirst: boolean;
  showFooter: boolean;
  showOrderCode: boolean;
  showTable: boolean;
  showTime: boolean;
  showWaiter: boolean;
  showEventTitle: boolean;
  showItemNotes: boolean;
  showGeneralNotes: boolean;
  showSeparators: boolean;
  showItemPrice: boolean;
  showTotal: boolean;
  showCategoryHeaders: boolean;
  tableLabel: string;
  timeLabel: string;
  waiterLabel: string;
  commentLabel: string;
  totalLabel: string;
  footerLabel: string;
  notePrefix: string;
  separator: string;
  itemTemplate: string;
  printerLabel: string;
};

/* ------------------------------------------------------------------ settings */

const HISTORY_SUFFIX = "_history";
const MISSING = " missing ";

/** Mirrors the Android settings lookup, including its per-printer and per-profile key layering. */
export class PrintSettings {
  private readonly values: Map<string, string>;

  constructor(settings: SettingRow[]) {
    this.values = new Map(settings.map((row) => [row.key, String(row.value ?? "")]));
  }

  private get(key: string, fallback: string): string {
    const value = this.values.get(key);
    return value === undefined ? fallback : value;
  }

  private printerKey(key: string, printerId: number): string {
    return printerId > 0 ? `${key}_printer_${printerId}` : key;
  }

  private profileKey(key: string, profile: PrintProfile): string {
    return profile === "history" ? key + HISTORY_SUFFIX : key;
  }

  printerValue(key: string, fallback: string, printerId: number): string {
    const shared = this.get(key, fallback);
    return printerId > 0 ? this.get(this.printerKey(key, printerId), shared) : shared;
  }

  /** Per-printer history values follow that printer's order profile until customized. */
  profileValue(key: string, fallback: string, profile: PrintProfile, printerId: number): string {
    const printerOrderValue = this.printerValue(key, fallback, printerId);
    if (profile !== "history") {
      return printerOrderValue;
    }
    const sharedHistory = this.get(key + HISTORY_SUFFIX, MISSING);
    const historyFallback = sharedHistory === MISSING ? printerOrderValue : sharedHistory;
    return printerId > 0
      ? this.get(this.printerKey(this.profileKey(key, profile), printerId), historyFallback)
      : historyFallback;
  }

  bool(key: string, fallback: boolean, profile: PrintProfile, printerId: number): boolean {
    return this.profileValue(key, fallback ? "1" : "0", profile, printerId) !== "0";
  }

  text(key: string, fallback: string, profile: PrintProfile, printerId: number): string {
    const value = this.profileValue(key, fallback, profile, printerId);
    return value.trim() === "" ? fallback : value;
  }

  ids(key: string, profile: PrintProfile, printerId: number): number[] {
    const csv = this.profileValue(key, "", profile, printerId);
    const ids: number[] = [];
    for (const part of csv.split(",")) {
      const id = Number.parseInt(part.trim(), 10);
      if (Number.isFinite(id) && id > 0 && !ids.includes(id)) {
        ids.push(id);
      }
    }
    return ids;
  }
}

function textScaleOf(settings: PrintSettings, profile: PrintProfile, printerId: number): number {
  const mode = settings.profileValue("print_text_size", "normal", profile, printerId);
  if (mode === "small") return 0.88;
  if (mode === "large") return 1.12;
  return 1;
}

function detailTextScaleOf(settings: PrintSettings, profile: PrintProfile, printerId: number): number {
  const mode = settings.profileValue("print_detail_text_size", "normal", profile, printerId);
  if (mode === "large") return 1.25;
  if (mode === "extra_large") return 1.5;
  return 1;
}

function itemSpacingOf(settings: PrintSettings, profile: PrintProfile, printerId: number): number {
  const mode = settings.profileValue("print_item_spacing", "normal", profile, printerId);
  if (mode === "compact") return 10;
  if (mode === "wide") return 42;
  return 26;
}

function itemOrderModeOf(settings: PrintSettings, profile: PrintProfile, printerId: number): string {
  const value = settings.profileValue("print_item_order", "selection", profile, printerId);
  return value === "category" || value === "category_headers" ? value : "selection";
}

export function buildRenderOptions(
  settings: PrintSettings,
  profile: PrintProfile,
  printer: PrinterConfig
): RenderOptions {
  const printerId = printer.id;
  const paper58 = settings.printerValue("print_paper_width", "80", printerId) === "58";
  const printerLabel = (printer.nickname || printer.device_name || "").trim();
  const raw = {
    paperWidthPx: paper58 ? 384 : 576,
    textScale: textScaleOf(settings, profile, printerId),
    detailTextScale: detailTextScaleOf(settings, profile, printerId),
    itemSpacingPx: itemSpacingOf(settings, profile, printerId),
    eventTitleFirst:
      settings.profileValue("print_header_style", "table_code", profile, printerId) === "event_title",
    showFooter: settings.bool("print_footer_enabled", true, profile, printerId),
    showOrderCode: settings.bool("print_show_order_code", true, profile, printerId),
    showTable: settings.bool("print_show_table", true, profile, printerId),
    showTime: settings.bool("print_show_time", true, profile, printerId),
    showWaiter: settings.bool("print_show_waiter", true, profile, printerId),
    showEventTitle: settings.bool("print_show_event_title", true, profile, printerId),
    showItemNotes: settings.bool("print_show_item_notes", true, profile, printerId),
    showGeneralNotes: settings.bool("print_show_general_notes", true, profile, printerId),
    showSeparators: settings.bool("print_show_separators", true, profile, printerId),
    showItemPrice: settings.bool("print_show_item_price", false, profile, printerId),
    showTotal: settings.bool("print_show_total", false, profile, printerId),
    showCategoryHeaders: itemOrderModeOf(settings, profile, printerId) === "category_headers",
    tableLabel: settings.text("print_label_table", "ΤΡΑΠΕΖΙ", profile, printerId),
    timeLabel: settings.text("print_label_time", "Ώρα", profile, printerId),
    waiterLabel: settings.text("print_label_waiter", "Σερβιτόρος", profile, printerId),
    commentLabel: settings.text("print_label_comment", "Σχόλιο", profile, printerId),
    totalLabel: settings.text("print_label_total", "Σύνολο", profile, printerId),
    footerLabel: settings.text("print_label_footer", "ΤΕΛΟΣ", profile, printerId),
    notePrefix: settings.profileValue("print_note_prefix", "  - ", profile, printerId),
    separator: settings.text(
      "print_separator",
      "--------------------------------",
      profile,
      printerId
    ),
    itemTemplate: settings.text("print_item_template", "{qty}x {name}", profile, printerId),
    printerLabel
  };
  // The Java constructor clamps; keep the same bounds so a stray setting cannot break the layout.
  return {
    ...raw,
    paperWidthPx: Math.max(320, raw.paperWidthPx),
    textScale: Math.max(0.75, Math.min(1.25, raw.textScale)),
    detailTextScale: Math.max(0.75, Math.min(1.6, raw.detailTextScale)),
    itemSpacingPx: Math.max(6, raw.itemSpacingPx)
  };
}

/* ------------------------------------------------------- item name variations */

export function variationMultiplier(variation: string): number {
  const normalized = (variation ?? "").trim().replace(/,/g, ".");
  if (normalized === "") {
    return 1;
  }
  let whole = 0;
  let fractionPart = normalized;
  const space = normalized.indexOf(" ");
  if (space > 0 && normalized.includes("/")) {
    const parsed = Number.parseFloat(normalized.slice(0, space).trim());
    if (!Number.isFinite(parsed)) {
      return 1;
    }
    whole = parsed;
    fractionPart = normalized.slice(space + 1).trim();
  }
  if (fractionPart.includes("/")) {
    const parts = fractionPart.split("/");
    if (parts.length === 2) {
      const numerator = Number.parseFloat(parts[0].trim());
      const denominator = Number.parseFloat(parts[1].trim());
      if (Number.isFinite(numerator) && Number.isFinite(denominator) && denominator > 0) {
        const value = whole + numerator / denominator;
        return value > 0 ? value : 1;
      }
    }
    return 1;
  }
  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

export function formatItemNameWithVariation(
  storedName: string,
  hideUnitVariation: boolean,
  variationInQty: boolean
): string {
  const name = (storedName ?? "").trim();
  const splitAt = name.lastIndexOf(" - ");
  if (splitAt <= 0) {
    return name;
  }
  const base = name.slice(0, splitAt).trim();
  const variation = name.slice(splitAt + 3).trim();
  if (!/^[0-9][0-9.,/ ]*$/.test(variation)) {
    return name;
  }
  if (hideUnitVariation && variationMultiplier(variation) === 1) {
    return base;
  }
  if (variationInQty) {
    return `(${variation}) ${base}`;
  }
  return name;
}

/* -------------------------------------------------------------- ticket build */

function joinNotes(parts: string[]): string {
  // ", " matches the Android app's join(); the ticket must read identically on both.
  return parts.filter((part) => part.trim() !== "").join(", ");
}

function splitOptions(value: string | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry !== "");
}

function splitVariationOptions(value: string | undefined): string[] {
  const clean = (value ?? "").trim();
  if (clean === "") {
    return [];
  }
  const raw = clean.includes("\n")
    ? clean.split(/\r?\n/)
    : clean.includes(";")
      ? clean.split(";")
      : [clean];
  return raw.map((entry) => entry.trim()).filter((entry) => entry !== "");
}

export function ticketLineNotes(line: OrderLine): string {
  const notes: string[] = [];
  if (line.without_options) notes.push("Χωρίς: " + line.without_options);
  if (line.extras) notes.push("Extra: " + optionLabels(line.extras).join(", "));
  if (line.counters) notes.push("Μετρητές: " + line.counters);
  if (line.takeaway) notes.push("Πακέτο");
  if (line.comment) notes.push("Σχόλιο: " + line.comment);
  return joinNotes(notes);
}

function orderLineKey(line: OrderLine): string {
  return [
    line.product_id,
    line.name,
    line.without_options,
    line.extras,
    line.counters,
    line.comment,
    line.takeaway ? "true" : "false"
  ].join("|");
}

/**
 * The order's contents as they stand now, matching fullOrderTicketItems() in the Android app.
 *
 * Corrections are recorded as extra negative lines instead of edits, so identical lines are grouped
 * and summed here; a product corrected down to nothing drops off the slip rather than printing as a
 * removal, because these slips describe what the order *is*, not how it got there.
 */
function currentOrderItems(lines: OrderLine[]): TicketItem[] {
  const grouped = new Map<string, TicketItem>();
  for (const line of lines) {
    const notes = ticketLineNotes(line);
    const printedName = line.print_name || line.name;
    const key = [line.product_id, line.name, line.print_name, notes, line.price].join("\u001f");
    const existing = grouped.get(key);
    if (existing) {
      existing.quantity += line.quantity;
      continue;
    }
    grouped.set(key, baseItem(line.quantity, printedName, notes, line.price, line.product_id));
  }
  return [...grouped.values()].filter((item) => item.quantity > 0);
}

function baseItem(quantity: number, name: string, notes: string, unitPrice: number, productId: number): TicketItem {
  return {
    quantity,
    name,
    notes,
    unitPrice,
    productId,
    categoryName: "",
    categorySortOrder: Number.MAX_SAFE_INTEGER
  };
}

/** Builds the ticket for one order event, matching orderEventTicket() in the Android app. */
export function orderEventTicket(
  order: OrderRecord,
  event: OrderEvent,
  eventLines: OrderLine[],
  allOrderLines: OrderLine[]
): Ticket {
  const items: TicketItem[] = [];
  const isCorrection = (event.type ?? "").includes("ΔΙΟΡΘΩΣΗ");
  // A move changes nothing about what was ordered, so the slip carries no items at all: the note
  // "Από τραπέζι X σε τραπέζι Y" is the whole message, and the kitchen keeps the ticket it has.
  const isTableChange = (event.type ?? "").includes("ΑΛΛΑΓΗ ΤΡΑΠΕΖΙΟΥ");
  if (isCorrection) {
    // A correction ticket shows where each product ENDS UP, not the delta; products corrected
    // away print as ΑΦΑΙΡΕΣΗ.
    const totals = new Map<string, number>();
    for (const line of allOrderLines) {
      const key = orderLineKey(line);
      totals.set(key, (totals.get(key) ?? 0) + line.quantity);
    }
    const printed = new Set<string>();
    for (const line of eventLines) {
      const key = orderLineKey(line);
      if (printed.has(key)) {
        continue;
      }
      printed.add(key);
      const total = totals.get(key) ?? 0;
      const printedName = line.print_name || line.name;
      if (total > 0) {
        items.push(baseItem(total, printedName, ticketLineNotes(line), line.price, line.product_id));
      } else {
        const removed = Math.max(1, Math.abs(line.quantity));
        items.push(
          baseItem(removed, "ΑΦΑΙΡΕΣΗ: " + printedName, ticketLineNotes(line), line.price, line.product_id)
        );
      }
    }
  } else if (!isTableChange) {
    for (const line of eventLines) {
      const quantity = Math.max(1, Math.abs(line.quantity));
      const printedName = line.print_name || line.name;
      const name = line.quantity < 0 ? "ΑΦΑΙΡΕΣΗ: " + printedName : printedName;
      items.push(baseItem(quantity, name, ticketLineNotes(line), line.price, line.product_id));
    }
  }
  if (items.length === 0 && (event.type ?? "").includes("ΑΚΥΡΩΣΗ")) {
    items.push(baseItem(1, "ΑΚΥΡΩΣΗ ΟΛΗΣ ΤΗΣ ΠΑΡΑΓΓΕΛΙΑΣ", event.note ?? "", 0, 0));
  }
  return {
    title: event.type ?? "",
    table: order.table_label ?? "",
    waiter: (event.waiter || order.waiter) ?? "",
    generalNotes: (event.type ?? "").includes("ΑΚΥΡΩΣΗ") ? "" : event.note ?? "",
    orderNumber: order.order_number,
    createdAt: event.created_at > 0 ? event.created_at : order.created_at,
    items
  };
}

/** Builds a whole-order reprint, the "history" profile. */
export function historyTicket(order: OrderRecord, lines: OrderLine[]): Ticket {
  const items = currentOrderItems(lines);
  if (items.length === 0) {
    items.push(baseItem(1, "ΧΩΡΙΣ ΕΝΕΡΓΑ ΠΡΟΪΟΝΤΑ", "", 0, 0));
  }
  return {
    title: "ΕΠΑΝΕΚΤΥΠΩΣΗ",
    table: order.table_label ?? "",
    waiter: order.waiter ?? "",
    generalNotes: "Επανεκτύπωση - Κατάσταση: " + (order.status ?? ""),
    orderNumber: order.order_number,
    createdAt: order.created_at,
    items
  };
}

function resolvedCategoryOrder(
  categories: Category[],
  settings: PrintSettings,
  profile: PrintProfile,
  printerId: number
): Category[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const resolved: Category[] = [];
  const added = new Set<number>();
  for (const id of settings.ids("print_category_order", profile, printerId)) {
    const category = byId.get(id);
    if (category && !added.has(category.id)) {
      added.add(category.id);
      resolved.push(category);
    }
  }
  for (const category of categories) {
    if (!added.has(category.id)) {
      added.add(category.id);
      resolved.push(category);
    }
  }
  const alwaysLast = new Set(settings.ids("print_categories_last", profile, printerId));
  const pinned = resolved.filter((category) => alwaysLast.has(category.id));
  const rest = resolved.filter((category) => !alwaysLast.has(category.id));
  return [...rest, ...pinned];
}

/** Applies one printer's naming, category ranking and ordering, per prepareTicketForPrinter(). */
export function prepareTicketForPrinter(
  ticket: Ticket,
  settings: PrintSettings,
  profile: PrintProfile,
  printerId: number,
  products: Product[],
  categories: Category[]
): Ticket {
  const productsById = new Map(products.map((product) => [product.id, product]));
  const categoriesById = new Map(categories.map((category) => [category.id, category]));
  const ranks = new Map<number, number>();
  resolvedCategoryOrder(categories, settings, profile, printerId).forEach((category, index) => {
    ranks.set(category.id, index);
  });

  const hideUnitVariation = settings.bool("print_hide_unit_variation", true, profile, printerId);
  const variationInQty = settings.bool("print_variation_in_qty", true, profile, printerId);

  const items = ticket.items.map((item) => {
    let name = item.name;
    let prefix = "";
    if (name.startsWith("ΑΦΑΙΡΕΣΗ: ")) {
      prefix = "ΑΦΑΙΡΕΣΗ: ";
      name = name.slice(prefix.length);
    }
    const product = productsById.get(item.productId);
    const category = product ? categoriesById.get(product.category_id) : undefined;
    const categoryName = category
      ? category.name
      : product
        ? "ΧΩΡΙΣ ΚΑΤΗΓΟΡΙΑ"
        : item.categoryName;
    const rank = category ? ranks.get(category.id) : undefined;
    return {
      ...item,
      name: prefix + formatItemNameWithVariation(name, hideUnitVariation, variationInQty),
      categoryName,
      categorySortOrder: rank === undefined ? item.categorySortOrder : rank
    };
  });

  if (itemOrderModeOf(settings, profile, printerId) !== "selection") {
    // Stable sort, matching Collections.sort's guarantee.
    items.sort((left, right) => left.categorySortOrder - right.categorySortOrder);
  }
  return { ...ticket, items };
}

/* ------------------------------------------------------------------ rendering */

type Paint = { size: number; bold: boolean };

type Line = {
  text: string;
  paint: Paint;
  rightText: string;
  rightPaint: Paint;
  height: number;
};

function fontOf(paint: Paint): string {
  return `${paint.bold ? "bold " : ""}${paint.size}px Roboto, "Helvetica Neue", Arial, sans-serif`;
}

function fallback(value: string, alternative: string): string {
  const clean = (value ?? "").trim();
  return clean === "" ? alternative : clean;
}

function formatMoney(amount: number): string {
  return `${amount.toFixed(2)} €`;
}

function formattedTime(createdAt: number): string {
  const date = new Date(createdAt > 0 ? createdAt : Date.now());
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())} ${pad(date.getDate())}/${pad(
    date.getMonth() + 1
  )}`;
}

function printableTitle(title: string): string {
  const normalized = title.trim().toUpperCase();
  if (normalized.includes("ΣΥΜΠΛΗΡΩΜΑΤΙΚΗ")) return "ΣΥΜΠΛ. ΠΑΡΑΓΓΕΛΙΑ";
  if (normalized.includes("ΔΙΟΡΘΩΣΗ")) return "ΔΙΟΡΘ. ΠΑΡΑΓΓΕΛΙΑΣ";
  if (normalized.includes("ΑΚΥΡΩΣΗ")) return "ΑΚΥΡΩΣΗ ΠΑΡΑΓΓ.";
  if (normalized.includes("ΑΡΧΙΚΗ")) return "ΠΑΡΑΓΓΕΛΙΑ";
  return title.trim();
}

function printableFooterTitle(title: string): string {
  if (title === "ΣΥΜΠΛ. ΠΑΡΑΓΓΕΛΙΑ") return "ΣΥΜΠΛ. ΠΑΡΑΓΓ.";
  // The footer reads "ΤΕΛΟΣ <τίτλου>", so the title takes its genitive form.
  if (title === "ΠΑΡΑΓΓΕΛΙΑ") return "ΠΑΡΑΓΓΕΛΙΑΣ";
  if (title === "ΕΠΑΝΕΚΤΥΠΩΣΗ") return "ΕΠΑΝΕΚΤΥΠΩΣΗΣ";
  if (title === "ΑΛΛΑΓΗ ΤΡΑΠΕΖΙΟΥ") return "ΑΛΛΑΓΗΣ ΤΡΑΠΕΖΙΟΥ";
  return title;
}

function labelValue(label: string, value: string): string {
  const cleanValue = (value ?? "").trim();
  const cleanLabel = (label ?? "").trim();
  return cleanLabel === "" ? cleanValue : `${cleanLabel}: ${cleanValue}`;
}

class LayoutContext {
  // A plain field rather than a constructor parameter property, so Node can strip the types
  // directly when running the unit tests.
  private readonly ctx: CanvasRenderingContext2D;

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  measure(text: string, paint: Paint): number {
    this.ctx.font = fontOf(paint);
    return this.ctx.measureText(text).width;
  }

  /** Mirrors Rect.top from Paint.getTextBounds: pixels above the baseline. */
  ascent(text: string, paint: Paint): number {
    this.ctx.font = fontOf(paint);
    const metrics = this.ctx.measureText(text === "" ? " " : text);
    const value = metrics.actualBoundingBoxAscent;
    return Number.isFinite(value) && value > 0 ? Math.ceil(value) : 0;
  }
}

function line(text: string, paint: Paint, height: number, rightText = "", rightPaint?: Paint): Line {
  return { text, paint, height, rightText: rightText ?? "", rightPaint: rightPaint ?? paint };
}

function addWrapped(
  layout: LayoutContext,
  lines: Line[],
  text: string,
  paint: Paint,
  maxWidth: number,
  lineHeight: number
): void {
  const words = text.split(/\s+/);
  let current = "";
  for (const word of words) {
    const candidate = current.length === 0 ? word : `${current} ${word}`;
    if (layout.measure(candidate, paint) <= maxWidth) {
      current = candidate;
    } else {
      if (current.length > 0) {
        lines.push(line(current, paint, lineHeight));
        current = "";
      }
      if (layout.measure(word, paint) <= maxWidth) {
        current = word;
      } else {
        splitLongWord(layout, lines, word, paint, maxWidth, lineHeight);
      }
    }
  }
  if (current.length > 0) {
    lines.push(line(current, paint, lineHeight));
  }
}

function splitLongWord(
  layout: LayoutContext,
  lines: Line[],
  word: string,
  paint: Paint,
  maxWidth: number,
  lineHeight: number
): void {
  let part = "";
  for (const character of word) {
    const candidate = part + character;
    if (layout.measure(candidate, paint) <= maxWidth) {
      part = candidate;
    } else {
      if (part.length > 0) {
        lines.push(line(part, paint, lineHeight));
      }
      part = character;
    }
  }
  if (part.length > 0) {
    lines.push(line(part, paint, lineHeight));
  }
}

function itemRowText(item: TicketItem, options: RenderOptions): string {
  const lineTotal = formatMoney(item.quantity * item.unitPrice);
  let row = options.itemTemplate
    .replace("{qty}", String(item.quantity))
    .replace("{name}", item.name)
    .replace("{notes}", options.showItemNotes ? item.notes : "")
    .replace("{price}", item.unitPrice > 0 ? formatMoney(item.unitPrice) : "")
    .replace("{line_total}", item.unitPrice > 0 ? lineTotal : "");
  if (
    options.showItemPrice &&
    item.unitPrice > 0 &&
    !options.itemTemplate.includes("{price}") &&
    !options.itemTemplate.includes("{line_total}")
  ) {
    row = `${row}  -  ${lineTotal}`;
  }
  return row.trim();
}

function buildTemplateLines(
  layout: LayoutContext,
  ticket: Ticket,
  options: RenderOptions,
  contentWidth: number
): Line[] {
  const scaled = (base: number) => Math.max(1, Math.round(base * options.textScale));
  const detailScaled = (base: number) =>
    Math.max(1, Math.round(base * options.textScale * options.detailTextScale));

  const title: Paint = { size: 42 * options.textScale, bold: true };
  const bold: Paint = { size: 32 * options.textScale, bold: true };
  const regular: Paint = { size: 28 * options.textScale, bold: false };
  const details: Paint = { size: 24 * options.textScale * options.detailTextScale, bold: false };
  const comments: Paint = { size: 28 * options.textScale * options.detailTextScale, bold: false };
  const categoryHeader: Paint = { size: 28 * options.textScale, bold: true };

  const lines: Line[] = [];
  const ticketTitle = printableTitle(fallback(ticket.title, "ΠΑΡΑΓΓΕΛΙΑ"));
  const orderCode = options.showOrderCode
    ? ticket.orderNumber > 0
      ? `#${ticket.orderNumber}`
      : "#--"
    : "";

  if (options.eventTitleFirst) {
    if (options.showEventTitle) {
      lines.push(line(ticketTitle, title, scaled(56), orderCode, bold));
    }
    if (options.showTable) {
      lines.push(
        line(
          labelValue(options.tableLabel, fallback(ticket.table, "-")),
          bold,
          scaled(42),
          options.showEventTitle ? "" : orderCode,
          bold
        )
      );
    }
  } else {
    const left = options.showTable
      ? labelValue(options.tableLabel, fallback(ticket.table, "-"))
      : options.showEventTitle
        ? ticketTitle
        : "";
    if (left !== "" || orderCode !== "") {
      lines.push(line(left, title, scaled(56), orderCode, bold));
    }
    if (options.showEventTitle && options.showTable && ticketTitle !== "ΠΑΡΑΓΓΕΛΙΑ") {
      addWrapped(layout, lines, ticketTitle, bold, contentWidth, scaled(42));
    }
  }
  if (options.showTime) {
    lines.push(line(labelValue(options.timeLabel, formattedTime(ticket.createdAt)), regular, scaled(38)));
  }
  if (options.showWaiter && ticket.waiter !== "") {
    lines.push(line(labelValue(options.waiterLabel, ticket.waiter), regular, scaled(38)));
  }
  if (options.printerLabel !== "") {
    addWrapped(layout, lines, "Εκτυπωτής: " + options.printerLabel, regular, contentWidth, scaled(38));
  }
  if (options.showSeparators) {
    lines.push(line(options.separator, regular, scaled(34)));
  }

  let total = 0;
  let previousCategory = "";
  let previousCategoryOrder = Number.MIN_SAFE_INTEGER;
  ticket.items.forEach((item, index) => {
    if (
      options.showCategoryHeaders &&
      item.categoryName !== "" &&
      (item.categorySortOrder !== previousCategoryOrder || item.categoryName !== previousCategory)
    ) {
      addWrapped(layout, lines, item.categoryName, categoryHeader, contentWidth, scaled(38));
      previousCategory = item.categoryName;
      previousCategoryOrder = item.categorySortOrder;
    }
    total += item.quantity * item.unitPrice;
    const inlineDetails =
      options.showItemNotes && item.notes !== "" && options.itemTemplate.includes("{notes}");
    const itemPaint: Paint = inlineDetails
      ? { size: 32 * options.textScale * options.detailTextScale, bold: true }
      : bold;
    const itemLineHeight = inlineDetails ? detailScaled(42) : scaled(42);
    addWrapped(layout, lines, itemRowText(item, options), itemPaint, contentWidth, itemLineHeight);
    if (options.showItemNotes && item.notes !== "" && !options.itemTemplate.includes("{notes}")) {
      addWrapped(layout, lines, options.notePrefix + item.notes, details, contentWidth, detailScaled(32));
    }
    lines.push(
      line(
        "",
        regular,
        index < ticket.items.length - 1
          ? options.itemSpacingPx
          : Math.max(8, Math.trunc(options.itemSpacingPx / 2))
      )
    );
  });

  if (options.showGeneralNotes && ticket.generalNotes !== "") {
    // On a slip with no items the header separator is already the line above, so a second one here
    // would print an empty box.
    if (options.showSeparators && ticket.items.length > 0) {
      lines.push(line(options.separator, regular, scaled(34)));
    }
    addWrapped(
      layout,
      lines,
      labelValue(options.commentLabel, ticket.generalNotes),
      comments,
      contentWidth,
      detailScaled(36)
    );
  }
  if (options.showTotal && total > 0) {
    if (options.showSeparators) {
      lines.push(line(options.separator, regular, scaled(34)));
    }
    addWrapped(
      layout,
      lines,
      labelValue(options.totalLabel, formatMoney(total)),
      bold,
      contentWidth,
      scaled(42)
    );
  }
  if (options.showFooter) {
    if (options.showSeparators) {
      lines.push(line(options.separator, regular, scaled(34)));
    }
    addWrapped(
      layout,
      lines,
      `${options.footerLabel} ${printableFooterTitle(ticketTitle)}`,
      bold,
      contentWidth,
      scaled(44)
    );
  }
  return lines;
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/** Lays the ticket out and returns the finished bitmap, matching TicketRenderer.render(). */
export function renderTicketCanvas(ticket: Ticket, options: RenderOptions): HTMLCanvasElement {
  const padding = options.paperWidthPx < WIDTH_80MM ? PADDING_58MM : PADDING_80MM;
  const measureCanvas = createCanvas(1, 1);
  const measureCtx = measureCanvas.getContext("2d");
  if (!measureCtx) {
    throw new Error("Ο browser δεν υποστηρίζει canvas για εκτύπωση.");
  }
  const layout = new LayoutContext(measureCtx);
  const lines = buildTemplateLines(layout, ticket, options, options.paperWidthPx - padding * 2);

  let height = padding * 2;
  for (const item of lines) {
    height += item.height;
  }
  const canvas = createCanvas(options.paperWidthPx, Math.max(height, 220));
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Ο browser δεν υποστηρίζει canvas για εκτύπωση.");
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000000";
  ctx.textBaseline = "alphabetic";

  const drawLayout = new LayoutContext(ctx);
  let y = padding;
  for (const item of lines) {
    const ascentOffset = Math.max(
      drawLayout.ascent(item.text, item.paint),
      Math.trunc(item.height * 0.75)
    );
    const descentOffset = Math.max(0, item.height - ascentOffset);
    y += ascentOffset;
    ctx.font = fontOf(item.paint);
    ctx.fillText(item.text, padding, y);
    if (item.rightText !== "") {
      ctx.font = fontOf(item.rightPaint);
      const rightX = options.paperWidthPx - padding - ctx.measureText(item.rightText).width;
      ctx.fillText(item.rightText, rightX, y);
    }
    y += descentOffset;
  }
  return canvas;
}

/* ------------------------------------------------------------------- ESC/POS */

/** GS v 0 raster, byte-for-byte the same framing EscPosPrinter.java produces. */
export function escPosPayloadFromCanvas(canvas: HTMLCanvasElement): Uint8Array {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Ο browser δεν υποστηρίζει canvas για εκτύπωση.");
  }
  const width = canvas.width;
  const height = canvas.height;
  const widthBytes = Math.floor((width + 7) / 8);
  const pixels = ctx.getImageData(0, 0, width, height).data;

  const raster = new Uint8Array(8 + widthBytes * height);
  raster[0] = 0x1d;
  raster[1] = 0x76;
  raster[2] = 0x30;
  raster[3] = 0x00;
  raster[4] = widthBytes & 0xff;
  raster[5] = (widthBytes >> 8) & 0xff;
  raster[6] = height & 0xff;
  raster[7] = (height >> 8) & 0xff;

  let cursor = 8;
  for (let y = 0; y < height; y += 1) {
    for (let xByte = 0; xByte < widthBytes; xByte += 1) {
      let value = 0;
      for (let bit = 0; bit < 8; bit += 1) {
        const x = xByte * 8 + bit;
        if (x < width) {
          const offset = (y * width + x) * 4;
          const average = Math.floor((pixels[offset] + pixels[offset + 1] + pixels[offset + 2]) / 3);
          if (average < 180) {
            value |= 0x80 >> bit;
          }
        }
      }
      raster[cursor] = value;
      cursor += 1;
    }
  }

  const payload = new Uint8Array(2 + raster.length + 3 + 4);
  payload.set([0x1b, 0x40], 0);
  payload.set(raster, 2);
  payload.set([0x0a, 0x0a, 0x0a], 2 + raster.length);
  payload.set([0x1d, 0x56, 0x41, 0x10], 2 + raster.length + 3);
  return payload;
}

export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

export type PrinterPayload = {
  printer_id: number;
  payload_base64?: string;
  payload_gzip_base64?: string;
};

/**
 * Compresses a ticket, or returns null when this browser cannot.
 *
 * A ticket raster is mostly white, so gzip typically shrinks it by more than an order of
 * magnitude. That matters because the waiter is often on mobile data, where the upload dominates
 * how long "send order" appears to take.
 */
async function gzipBase64(bytes: Uint8Array): Promise<string | null> {
  if (typeof CompressionStream === "undefined") {
    return null;
  }
  try {
    const compressed = new Blob([bytes as BlobPart])
      .stream()
      .pipeThrough(new CompressionStream("gzip"));
    return toBase64(new Uint8Array(await new Response(compressed).arrayBuffer()));
  } catch {
    return null;
  }
}

/** Renders one ticket per printer and returns the payloads the server expects. */
export async function renderPrinterPayloads(input: {
  ticket: Ticket;
  profile: PrintProfile;
  printers: PrinterConfig[];
  settings: SettingRow[];
  products: Product[];
  categories: Category[];
  routing?: Record<string, number[]>;
}): Promise<PrinterPayload[]> {
  // Wait for webfonts before measuring anything. The server keys a print job by the sha256 of its
  // payload, so if one attempt lays the ticket out in a fallback face and a retry uses the real
  // one, the bytes differ and the retry is refused as a payload mismatch - permanently.
  try {
    await document.fonts?.ready;
  } catch {
    // A browser without the font-loading API just proceeds; its layout is deterministic anyway.
  }
  const settings = new PrintSettings(input.settings);
  const payloads: PrinterPayload[] = [];
  for (const printer of input.printers) {
    const routedProductIds = input.routing?.[String(printer.id)];
    const sourceTicket = routedProductIds
      ? { ...input.ticket, items: input.ticket.items.filter((item) => routedProductIds.includes(item.productId)) }
      : input.ticket;
    const prepared = prepareTicketForPrinter(
      sourceTicket,
      settings,
      input.profile,
      printer.id,
      input.products,
      input.categories
    );
    const options = buildRenderOptions(settings, input.profile, printer);
    const canvas = renderTicketCanvas(prepared, options);
    const bytes = escPosPayloadFromCanvas(canvas);
    const compressed = await gzipBase64(bytes);
    payloads.push(
      compressed
        ? { printer_id: printer.id, payload_gzip_base64: compressed }
        : { printer_id: printer.id, payload_base64: toBase64(bytes) }
    );
  }
  if (payloads.length === 0) {
    throw new Error("Δεν υπάρχει ενεργός εκτυπωτής.");
  }
  return payloads;
}

/**
 * Builds the ticket used by "test print".
 *
 * It is drawn from the real menu so the test proves what matters: that this shop's product names,
 * prices and category ordering come out right on paper. The fixed Greek sample is only a fallback
 * for a business that has not entered any products yet.
 */
export function testTicket(setup: Setup | null, waiter = "admin"): Ticket {
  return {
    title: "ΔΟΚΙΜΗ ΕΚΤΥΠΩΤΗ",
    table: "20",
    waiter,
    generalNotes: "Δοκιμή layout",
    orderNumber: 123,
    createdAt: Date.now(),
    items: previewMenuItems(setup)
  };
}

/** One sample note per product, mirroring previewProductNotes() in the Android app. */
function previewProductNotes(product: Product): string {
  const notes: string[] = [];
  const without = splitOptions(product.without_options);
  if (without.length > 0) {
    notes.push("Χωρίς: " + without[0]);
  }
  const extras = optionLabels(product.extras);
  if (extras.length > 0) {
    notes.push("Extra: " + extras[0]);
  }
  const counters = splitOptions(product.counter_options);
  if (counters.length > 0) {
    // Counter entries carry a label and a limit; only the label belongs on paper.
    notes.push("Μετρητές: " + counters[0].split(/[:|]/)[0].trim() + " 1");
  }
  if (notes.length === 0) {
    notes.push("Σχόλιο: preview");
  }
  return joinNotes(notes);
}

/**
 * Picks the products a test ticket shows, mirroring previewMenuItems() in the Android app: the
 * first product of each category so the ticket demonstrates category ordering, topped up from the
 * rest of the menu, with the first two swapped and fixed quantities.
 */
function previewMenuItems(setup: Setup | null): TicketItem[] {
  const categories = [...(setup?.categories ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const active = [...(setup?.products ?? [])]
    .filter((product) => product.active)
    .sort((a, b) => a.sort_order - b.sort_order);

  const chosen: Product[] = [];
  const chosenIds = new Set<number>();
  for (const category of categories) {
    const first = active.find((product) => product.category_id === category.id);
    if (first && !chosenIds.has(first.id)) {
      chosenIds.add(first.id);
      chosen.push(first);
    }
    if (chosen.length >= 3) {
      break;
    }
  }
  for (const product of active) {
    if (chosen.length >= 3) {
      break;
    }
    if (!chosenIds.has(product.id)) {
      chosenIds.add(product.id);
      chosen.push(product);
    }
  }
  if (chosen.length > 1) {
    [chosen[0], chosen[1]] = [chosen[1], chosen[0]];
  }

  const quantities = [2, 1, 3];
  const items: TicketItem[] = [];
  for (const product of chosen) {
    if (items.length >= 3) {
      break;
    }
    const variation = splitVariationOptions(product.variation_options)[0] ?? "";
    const printed = product.print_name || product.name;
    items.push({
      quantity: quantities[items.length],
      name: variation === "" ? printed : `${printed} - ${variation}`,
      notes: previewProductNotes(product),
      unitPrice: product.price * variationMultiplier(variation),
      productId: product.id,
      categoryName: "",
      categorySortOrder: 0
    });
  }

  if (items.length === 0) {
    // Nothing on the menu yet: the same placeholders the Android preview falls back to.
    const sample = (quantity: number, name: string, notes: string, unitPrice: number): TicketItem => ({
      quantity, name, notes, unitPrice, productId: 0, categoryName: "", categorySortOrder: 0
    });
    items.push(sample(2, "Προϊόν μενού", "Χωρίς: δείγμα | Extra: δείγμα", 4.5));
    items.push(sample(1, "Προϊόν επιλογής - 1 1/2", "Μετρητές: δείγμα 2", 7.2));
    items.push(sample(3, "Προϊόν σχόλιο", "Σχόλιο: δείγμα", 2.0));
  }
  return items;
}

/** Renders the test ticket for one printer, or null if this device cannot render it. */
export async function buildTestPayloads(
  setup: Setup | null,
  printerId: number,
  waiter = "admin"
): Promise<PrinterPayload[] | null> {
  if (!setup || typeof document === "undefined") {
    return null;
  }
  const printer = setup.printers.find((row) => row.id === printerId);
  if (!printer || !printer.enabled) {
    return null;
  }
  try {
    return await renderPrinterPayloads({
      ticket: testTicket(setup, waiter),
      profile: "order",
      printers: [printer],
      settings: setup.settings,
      products: setup.products,
      categories: setup.categories
    });
  } catch {
    return null;
  }
}

/** Builds the administrator's current operational-period report for one selected printer. */
export async function buildSalesReportPayloads(
  setup: Setup,
  report: SalesPeriodReport,
  printerId: number
): Promise<PrinterPayload[]> {
  const printer = setup.printers.find((row) => row.id === printerId && row.enabled);
  if (!printer) throw new Error("Ο επιλεγμένος εκτυπωτής δεν είναι διαθέσιμος.");

  const categoryOrder = new Map(report.categories.map((category, index) => [category.category_id, index]));
  const items: TicketItem[] = report.categories.flatMap((category) =>
    category.products.map((product) => ({
      quantity: product.quantity,
      name: product.name,
      notes: "",
      unitPrice: product.quantity ? product.total / product.quantity : 0,
      productId: product.product_id,
      categoryName: category.name,
      categorySortOrder: categoryOrder.get(category.category_id) ?? 0
    }))
  );
  report.extras.forEach((extra) => items.push({
    quantity: extra.quantity,
    name: extra.name,
    notes: extra.total > 0 ? `Χρέωση: ${extra.total.toFixed(2)} €` : "Χωρίς χρέωση",
    unitPrice: 0,
    productId: 0,
    categoryName: "ΠΡΟΣΘΕΤΑ",
    categorySortOrder: 1_000_000
  }));
  report.withouts.forEach((without) => items.push({
    quantity: without.quantity,
    name: `${without.product_name}: ${without.name}`,
    notes: "",
    unitPrice: 0,
    productId: 0,
    categoryName: "ΧΩΡΙΣ",
    categorySortOrder: 1_000_001
  }));

  const suffix = `_history_printer_${printerId}`;
  const forced = new Map<string, string>([
    [`print_show_order_code${suffix}`, "0"],
    [`print_show_table${suffix}`, "0"],
    [`print_show_waiter${suffix}`, "0"],
    [`print_show_event_title${suffix}`, "1"],
    [`print_show_item_notes${suffix}`, "1"],
    [`print_show_general_notes${suffix}`, "1"],
    [`print_show_item_price${suffix}`, "1"],
    [`print_show_total${suffix}`, "1"],
    [`print_item_order${suffix}`, "category_headers"],
    [`print_label_comment${suffix}`, "Σύνοψη"],
    [`print_label_total${suffix}`, "Σύνολο πωλήσεων"]
  ]);
  const settings = setup.settings.filter((row) => !forced.has(row.key));
  for (const [key, value] of forced) settings.push({ key, value });
  return renderPrinterPayloads({
    ticket: {
      title: "ΑΝΑΦΟΡΑ ΠΕΡΙΟΔΟΥ",
      table: "",
      waiter: "",
      generalNotes: `Περίοδος #${report.period_number} | Παραγγελίες: ${report.order_count} | Είδη: ${report.item_quantity}`,
      orderNumber: 0,
      createdAt: report.generated_at,
      items
    },
    profile: "history",
    printers: [printer],
    settings,
    products: setup.products,
    categories: setup.categories
  });
}

/**
 * The configured printers that should receive a table-change slip.
 *
 * Defaults to every enabled printer, so a shop with one printer or five behaves sensibly without
 * configuration, and each printer can opt out via print_table_change.
 */
export function tableChangePrinters(setup: Setup | null): number[] {
  if (!setup) {
    return [];
  }
  const settings = new PrintSettings(setup.settings);
  return setup.printers
    .filter((printer) => printer.enabled)
    .filter((printer) => settings.bool("print_table_change", true, "order", printer.id))
    .map((printer) => printer.id);
}

/**
 * Builds the per-printer payloads for one order, or null if this device cannot render them.
 *
 * Returning null rather than throwing is deliberate: the caller then falls back to queueing the
 * job by id, which is the path that already works, so a browser without canvas or a gap in the
 * cached setup degrades to today's behaviour instead of failing the print.
 */
export async function buildOrderPayloads(
  setup: Setup | null,
  orders: Orders | null,
  input: {
    orderId: number;
    eventId: number;
    profile: PrintProfile;
    printerIds: number[];
    routing?: Record<string, number[]>;
  }
): Promise<PrinterPayload[] | null> {
  if (!setup || !orders || typeof document === "undefined") {
    return null;
  }
  const order = orders.orders.find((row) => row.id === input.orderId);
  if (!order) {
    return null;
  }
  const printers = input.printerIds.length
    ? input.printerIds
        .map((id) => setup.printers.find((printer) => printer.id === id))
        .filter((printer): printer is PrinterConfig => Boolean(printer))
    : setup.printers.filter((printer) => printer.enabled);
  if (printers.length === 0 || printers.length !== (input.printerIds.length || printers.length)) {
    return null;
  }
  if (printers.some((printer) => !printer.enabled)) {
    return null;
  }

  let ticket: Ticket;
  if (input.profile === "history") {
    ticket = historyTicket(
      order,
      orders.order_items.filter((line) => line.order_id === order.id)
    );
  } else {
    const event = orders.order_events.find((row) => row.id === input.eventId);
    if (!event) {
      return null;
    }
    ticket = orderEventTicket(
      order,
      event,
      orders.order_items.filter((line) => line.event_id === event.id),
      orders.order_items.filter((line) => line.order_id === order.id)
    );
  }

  try {
    return await renderPrinterPayloads({
      ticket,
      profile: input.profile,
      printers,
      settings: setup.settings,
      products: setup.products,
      categories: setup.categories,
      routing: input.routing
    });
  } catch {
    return null;
  }
}
